import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import {
  vehicleTypesService,
  VehicleType,
  VehicleBodyType,
  CreateVehicleTypeRequest,
  UpdateVehicleTypeRequest,
  CreateVehicleBodyTypeRequest,
  UpdateVehicleBodyTypeRequest,
} from '@/services/vehicleTypesService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const VehicleTypesSettingsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  // Vehicle Types State
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(true);

  // Vehicle Body Types State
  const [vehicleBodyTypes, setVehicleBodyTypes] = useState<VehicleBodyType[]>([]);
  const [loadingBodyTypes, setLoadingBodyTypes] = useState(true);

  // Dialog States
  const [vehicleTypeDialog, setVehicleTypeDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    data?: VehicleType;
  }>({ open: false, mode: 'create' });

  const [bodyTypeDialog, setBodyTypeDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    data?: VehicleBodyType;
  }>({ open: false, mode: 'create' });

  // Form States
  const [vehicleTypeForm, setVehicleTypeForm] = useState<CreateVehicleTypeRequest>({
    name: '',
    description: '',
    display_order: 0,
  });

  const [bodyTypeForm, setBodyTypeForm] = useState<CreateVehicleBodyTypeRequest>({
    name: '',
    description: '',
    typical_seating_capacity: 5,
    display_order: 0,
  });

  // Load data
  useEffect(() => {
    loadVehicleTypes();
    loadVehicleBodyTypes();
  }, []);

  const loadVehicleTypes = async () => {
    try {
      setLoadingVehicleTypes(true);
      const types = await vehicleTypesService.getVehicleTypes();
      setVehicleTypes(types.sort((a, b) => a.displayOrder - b.displayOrder));
    } catch (error) {
      console.error('Failed to load vehicle types:', error);
      toast.error('Failed to load vehicle types');
    } finally {
      setLoadingVehicleTypes(false);
    }
  };

  const loadVehicleBodyTypes = async () => {
    try {
      setLoadingBodyTypes(true);
      const types = await vehicleTypesService.getVehicleBodyTypes();
      setVehicleBodyTypes(types.sort((a, b) => a.displayOrder - b.displayOrder));
    } catch (error) {
      console.error('Failed to load vehicle body types:', error);
      toast.error('Failed to load vehicle body types');
    } finally {
      setLoadingBodyTypes(false);
    }
  };

  // ==================== VEHICLE TYPES CRUD ====================

  const handleCreateVehicleType = async () => {
    try {
      await vehicleTypesService.createVehicleType(vehicleTypeForm);
      toast.success('Vehicle type created successfully');
      setVehicleTypeDialog({ open: false, mode: 'create' });
      setVehicleTypeForm({ name: '', description: '', display_order: 0 });
      loadVehicleTypes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create vehicle type');
    }
  };

  const handleUpdateVehicleType = async () => {
    if (!vehicleTypeDialog.data) return;

    try {
      const updateData: UpdateVehicleTypeRequest = {
        name: vehicleTypeForm.name,
        description: vehicleTypeForm.description,
        displayOrder: vehicleTypeForm.display_order,
      };

      await vehicleTypesService.updateVehicleType(vehicleTypeDialog.data.id, updateData);
      toast.success('Vehicle type updated successfully');
      setVehicleTypeDialog({ open: false, mode: 'create' });
      setVehicleTypeForm({ name: '', description: '', display_order: 0 });
      loadVehicleTypes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update vehicle type');
    }
  };

  const handleToggleVehicleTypeStatus = async (type: VehicleType) => {
    try {
      await vehicleTypesService.updateVehicleType(type.id, {
        isActive: !type.isActive,
      });
      toast.success(`Vehicle type ${type.isActive ? 'deactivated' : 'activated'}`);
      loadVehicleTypes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update vehicle type');
    }
  };

  const handleDeleteVehicleType = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This will deactivate it.`)) {
      return;
    }

    try {
      await vehicleTypesService.deleteVehicleType(id);
      toast.success('Vehicle type deleted successfully');
      loadVehicleTypes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete vehicle type');
    }
  };

  // ==================== VEHICLE BODY TYPES CRUD ====================

  const handleCreateBodyType = async () => {
    try {
      await vehicleTypesService.createVehicleBodyType(bodyTypeForm);
      toast.success('Vehicle body type created successfully');
      setBodyTypeDialog({ open: false, mode: 'create' });
      setBodyTypeForm({ name: '', description: '', typical_seating_capacity: 5, display_order: 0 });
      loadVehicleBodyTypes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create vehicle body type');
    }
  };

  const handleUpdateBodyType = async () => {
    if (!bodyTypeDialog.data) return;

    try {
      const updateData: UpdateVehicleBodyTypeRequest = {
        name: bodyTypeForm.name,
        description: bodyTypeForm.description,
        typicalSeatingCapacity: bodyTypeForm.typical_seating_capacity,
        displayOrder: bodyTypeForm.display_order,
      };

      await vehicleTypesService.updateVehicleBodyType(bodyTypeDialog.data.id, updateData);
      toast.success('Vehicle body type updated successfully');
      setBodyTypeDialog({ open: false, mode: 'create' });
      setBodyTypeForm({ name: '', description: '', typical_seating_capacity: 5, display_order: 0 });
      loadVehicleBodyTypes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update vehicle body type');
    }
  };

  const handleToggleBodyTypeStatus = async (type: VehicleBodyType) => {
    try {
      await vehicleTypesService.updateVehicleBodyType(type.id, {
        isActive: !type.isActive,
      });
      toast.success(`Vehicle body type ${type.isActive ? 'deactivated' : 'activated'}`);
      loadVehicleBodyTypes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update vehicle body type');
    }
  };

  const handleDeleteBodyType = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This will deactivate it.`)) {
      return;
    }

    try {
      await vehicleTypesService.deleteVehicleBodyType(id);
      toast.success('Vehicle body type deleted successfully');
      loadVehicleBodyTypes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete vehicle body type');
    }
  };

  // ==================== REORDERING ====================

  const handleReorderVehicleType = async (type: VehicleType, direction: 'up' | 'down') => {
    const currentIndex = vehicleTypes.findIndex(t => t.id === type.id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === vehicleTypes.length - 1)
    ) {
      return;
    }

    const newOrder = direction === 'up' ? type.displayOrder - 1 : type.displayOrder + 1;

    try {
      await vehicleTypesService.updateVehicleType(type.id, { displayOrder: newOrder });
      loadVehicleTypes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to reorder vehicle type');
    }
  };

  const handleReorderBodyType = async (type: VehicleBodyType, direction: 'up' | 'down') => {
    const currentIndex = vehicleBodyTypes.findIndex(t => t.id === type.id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === vehicleBodyTypes.length - 1)
    ) {
      return;
    }

    const newOrder = direction === 'up' ? type.displayOrder - 1 : type.displayOrder + 1;

    try {
      await vehicleTypesService.updateVehicleBodyType(type.id, { displayOrder: newOrder });
      loadVehicleBodyTypes();
    } catch (error: any) {
      toast.error(error.message || 'Failed to reorder vehicle body type');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Vehicle Types & Body Types
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage rental classifications (Economy, Luxury) and body styles (Sedan, SUV, Truck)
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Vehicle Types (Rental Tier)" />
          <Tab label="Vehicle Body Types (Physical Form)" />
        </Tabs>

        {/* VEHICLE TYPES TAB */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Rental classifications like Economy, Luxury, Executive
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setVehicleTypeForm({ name: '', description: '', display_order: vehicleTypes.length });
                setVehicleTypeDialog({ open: true, mode: 'create' });
              }}
            >
              Add Vehicle Type
            </Button>
          </Box>

          {loadingVehicleTypes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : vehicleTypes.length === 0 ? (
            <Alert severity="info">No vehicle types found. Add your first vehicle type to get started.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vehicleTypes.map((type, index) => (
                    <TableRow key={type.id}>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            onClick={() => handleReorderVehicleType(type, 'up')}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={index === vehicleTypes.length - 1}
                            onClick={() => handleReorderVehicleType(type, 'down')}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {type.typeName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {type.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={type.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={type.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={type.isActive}
                                onChange={() => handleToggleVehicleTypeStatus(type)}
                                size="small"
                              />
                            }
                            label=""
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              setVehicleTypeForm({
                                name: type.typeName,
                                description: type.description || '',
                                display_order: type.displayOrder,
                              });
                              setVehicleTypeDialog({ open: true, mode: 'edit', data: type });
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteVehicleType(type.id, type.typeName)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* VEHICLE BODY TYPES TAB */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Physical body styles like Sedan, SUV, Truck, Van
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setBodyTypeForm({ name: '', description: '', typical_seating_capacity: 5, display_order: vehicleBodyTypes.length });
                setBodyTypeDialog({ open: true, mode: 'create' });
              }}
            >
              Add Body Type
            </Button>
          </Box>

          {loadingBodyTypes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : vehicleBodyTypes.length === 0 ? (
            <Alert severity="info">No vehicle body types found. Add your first body type to get started.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Typical Seating</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vehicleBodyTypes.map((type, index) => (
                    <TableRow key={type.id}>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            onClick={() => handleReorderBodyType(type, 'up')}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={index === vehicleBodyTypes.length - 1}
                            onClick={() => handleReorderBodyType(type, 'down')}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {type.bodyName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {type.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {type.typicalSeating}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={type.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={type.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={type.isActive}
                                onChange={() => handleToggleBodyTypeStatus(type)}
                                size="small"
                              />
                            }
                            label=""
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              setBodyTypeForm({
                                name: type.bodyName,
                                description: type.description || '',
                                typical_seating_capacity: parseInt(type.typicalSeating) || 5,
                                display_order: type.displayOrder,
                              });
                              setBodyTypeDialog({ open: true, mode: 'edit', data: type });
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteBodyType(type.id, type.bodyName)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* VEHICLE TYPE CREATE/EDIT DIALOG */}
      <Dialog
        open={vehicleTypeDialog.open}
        onClose={() => setVehicleTypeDialog({ open: false, mode: 'create' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {vehicleTypeDialog.mode === 'create' ? 'Add Vehicle Type' : 'Edit Vehicle Type'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={vehicleTypeForm.name}
              onChange={(e) => setVehicleTypeForm({ ...vehicleTypeForm, name: e.target.value })}
              placeholder="e.g., Economy, Luxury, Executive"
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={vehicleTypeForm.description}
              onChange={(e) => setVehicleTypeForm({ ...vehicleTypeForm, description: e.target.value })}
              placeholder="Brief description of this rental tier"
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Display Order"
              type="number"
              value={vehicleTypeForm.display_order}
              onChange={(e) => setVehicleTypeForm({ ...vehicleTypeForm, display_order: parseInt(e.target.value) || 0 })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVehicleTypeDialog({ open: false, mode: 'create' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={vehicleTypeDialog.mode === 'create' ? handleCreateVehicleType : handleUpdateVehicleType}
            disabled={!vehicleTypeForm.name}
          >
            {vehicleTypeDialog.mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* BODY TYPE CREATE/EDIT DIALOG */}
      <Dialog
        open={bodyTypeDialog.open}
        onClose={() => setBodyTypeDialog({ open: false, mode: 'create' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {bodyTypeDialog.mode === 'create' ? 'Add Vehicle Body Type' : 'Edit Vehicle Body Type'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={bodyTypeForm.name}
              onChange={(e) => setBodyTypeForm({ ...bodyTypeForm, name: e.target.value })}
              placeholder="e.g., Sedan, SUV, Truck"
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={bodyTypeForm.description}
              onChange={(e) => setBodyTypeForm({ ...bodyTypeForm, description: e.target.value })}
              placeholder="Brief description of this body type"
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Typical Seating Capacity"
              type="number"
              value={bodyTypeForm.typical_seating_capacity}
              onChange={(e) => setBodyTypeForm({ ...bodyTypeForm, typical_seating_capacity: parseInt(e.target.value) || 5 })}
              fullWidth
            />
            <TextField
              label="Display Order"
              type="number"
              value={bodyTypeForm.display_order}
              onChange={(e) => setBodyTypeForm({ ...bodyTypeForm, display_order: parseInt(e.target.value) || 0 })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBodyTypeDialog({ open: false, mode: 'create' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={bodyTypeDialog.mode === 'create' ? handleCreateBodyType : handleUpdateBodyType}
            disabled={!bodyTypeForm.name}
          >
            {bodyTypeDialog.mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
